// src/features/streams/useStreamMutations.ts

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { createBatchMutationHook } from '@/shared/hooks/useBatchMutation';
import { MEDIA_QUERY_KEYS } from '@/lib/constants/media';
import { TAG_QUERY_KEYS } from '@/lib/constants/tag';
import { StreamApi } from './StreamApi';
import type { StreamCreateRequest, StreamMediaUpdateRequest, StreamSettingsUpdateRequest } from '@/lib/types/Stream';

// Batch mutations via shared factory (same pattern as videos/playlists)
export const useActivateStreams = createBatchMutationHook(
  StreamApi.activateStreams,
  (cId) => MEDIA_QUERY_KEYS.streams(cId),
);

export const useDeactivateStreams = createBatchMutationHook(
  StreamApi.deactivateStreams,
  (cId) => MEDIA_QUERY_KEYS.streams(cId),
);

export const useDeleteStreams = createBatchMutationHook(
  StreamApi.deleteStreams,
  (cId) => MEDIA_QUERY_KEYS.streams(cId),
);

// --- Detail-level mutations ---

/** Update stream media fields (general information). */
export function useUpdateStreamMedia(mediaId: number) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: StreamMediaUpdateRequest) =>
      StreamApi.updateMedia(selectedCId!, mediaId, data),
    onSuccess: (updatedStream) => {
      queryClient.setQueryData(
        MEDIA_QUERY_KEYS.stream(selectedCId!, mediaId),
        updatedStream,
      );
      queryClient.invalidateQueries({
        queryKey: MEDIA_QUERY_KEYS.streams(selectedCId!),
      });
      queryClient.invalidateQueries({
        queryKey: TAG_QUERY_KEYS.list(selectedCId!),
      });
    },
  });
}

/** Update stream settings (recording, offline, captions). */
export function useUpdateStreamSettings(mediaId: number) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: StreamSettingsUpdateRequest) =>
      StreamApi.updateStream(selectedCId!, mediaId, data),
    onSuccess: (updatedStream) => {
      queryClient.setQueryData(
        MEDIA_QUERY_KEYS.stream(selectedCId!, mediaId),
        updatedStream,
      );
      queryClient.invalidateQueries({
        queryKey: MEDIA_QUERY_KEYS.streams(selectedCId!),
      });
    },
  });
}

/** Inject ad break into live stream. */
export function useInjectAdBreak(mediaId: number) {
  const { selectedCId } = useAuth();

  return useMutation({
    mutationFn: (duration: number) =>
      StreamApi.injectAdBreak(selectedCId!, mediaId, duration),
  });
}

// Create is feature-specific (unique request shape, not a batch ID operation)
export function useCreateStream() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: StreamCreateRequest) =>
      StreamApi.createStream(selectedCId!, req),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: MEDIA_QUERY_KEYS.streams(selectedCId!),
      });
    },
  });
}
